import React, { useEffect, useState } from 'react';
import { Modal, Button, Card, Icon } from '@salesforce/design-system-react';
import CustomSpinnerOverlay from "../../../../utils/components/spinner/custom_spinner_overlay";
import { SCPDeploymentAPIServiceFactory } from "../../../../services/deployment/scp/factory";
import { GenericDeploymentSubmissionResponse } from "../../../../utils/types/deployments/genericDeploymentResponse";

interface AutoDeploymentConfirmationProps {
    isOpen: boolean;
    enable: boolean;
    selectedOperationID: string;
    selectedCommitID: string;
    selectedCommitTitle: string;
    selectedCommitDeveloper: string;
    onClose: () => void;
    onSuccess: (enabled: boolean) => void
    status: string
}

const SCPAutoDeploymentConfirmationModal: React.FC<AutoDeploymentConfirmationProps> = ({
                                                                                           isOpen,
                                                                                           enable,
                                                                                           selectedOperationID,
                                                                                           selectedCommitID,
                                                                                           selectedCommitTitle,
                                                                                           selectedCommitDeveloper,
                                                                                           onClose,
                                                                                           onSuccess,
                                                                                           status,
                                                                                       }) => {
    const [isInProgress, setIsInProgress] = useState<boolean>(false);
    const [apiResponse, setAPIResponse] = useState<GenericDeploymentSubmissionResponse | undefined>(undefined);
    const scpAPIService = SCPDeploymentAPIServiceFactory.getService();

    const handleSubmit = async () => {
        setIsInProgress(true);
        try {
            const response = await scpAPIService.setAutoMode(selectedOperationID, enable);
            response.success && onSuccess(enable)
            setAPIResponse(response)
        } catch (err) {
            console.error("API failed", err);
            setAPIResponse(new GenericDeploymentSubmissionResponse(`${err}`, false))
        } finally {
            setIsInProgress(false);
        }
    };

    const getActionInformation = () => {
        if (enable) {
            return (
                <p>
                    You are about to <strong>enable Auto Expansion</strong> for operation <strong>{selectedOperationID}</strong>.<br />
                    Once enabled, this operation will automatically be expanded for the remaining staggers with preconfigured bake times.<br /><br />
                    Are you sure you want to proceed?
                </p>
            );
        }
        return (
            <p>
                You are about to <strong>disable Auto Expansion</strong> for operation <strong>{selectedOperationID}</strong>.<br />
                Once disabled, this operation will have to be manually expanded for the remaining staggers with preconfigured bake times.<br /><br />
                Are you sure you want to proceed?
            </p>
        );
    };


    return (
        <Modal
            isOpen={isOpen}
            size="small"
            onRequestClose={onClose}
            dismissOnClickOutside={false}
            footer={
                <Button
                    label={enable ? "Enable" : "Disable"}
                    onClick={handleSubmit}
                    variant={enable ? "brand" : "destructive"}
                    disabled={isInProgress}
                />
            }
        >
            <Card
                heading={
                    <>
                        <strong className={enable ? "slds-text-color_success" : "slds-text-color_error"}>
                            {enable ? "Enable Auto Expand:" : "Disable Auto Expand:"}
                        </strong>
                        &nbsp;
                        <strong>{selectedOperationID}</strong>
                    </>
                }
                icon={<Icon category="standard" name="actions_and_buttons" size="small" />}
                className="slds-p-vertical_small slds-color__background_gray-3"
                bodyClassName="slds-p-right_medium slds-p-left_medium"
            >
                <p> {selectedCommitTitle || "No title provided"}</p>
            </Card>

            <div className="slds-grid slds-wrap">
                {selectedCommitDeveloper.length > 0 && <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Author: {selectedCommitDeveloper}
                </span>}
                {selectedCommitID.length > 0 && <span className="slds-m-top_x-small slds-m-bottom_small slds-m-horizontal--small slds-badge slds-box_link">
                    Commit ID: {selectedCommitID}
                </span>}
            </div>

            <div className="slds-p-around_medium">
                <div className="slds-box slds-box_x-small">
                    <div className="slds-text-title_caps slds-m-bottom_small">Confirmation Required</div>
                    <div className="slds-text-body_regular">
                        {getActionInformation()}
                    </div>
                </div>
            </div>


            {isInProgress &&
                <div className="slds-p-around_xx-large">
                    <CustomSpinnerOverlay size={"medium"} />
                </div>
            }
            {apiResponse && <>
                <Card hasNoHeader={true}>
                    {apiResponse.success ?
                        <div className="slds-m-around_medium slds-p-around_medium slds-box">
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_success">
                                Rollback Request Submitted Successfully!
                            </h2>
                        </div> :
                        <div className="slds-m-around_medium slds-p-around_medium slds-box">
                            <h2 className="slds-text-heading_medium slds-text-align_center slds-m-bottom_medium slds-text-color_destructive">
                                {apiResponse.message}
                            </h2>
                        </div>
                    }
                </Card>
            </>}

        </Modal>
    );
};

export default SCPAutoDeploymentConfirmationModal;
